/**
 * 관리 콘솔 › 시스템 설정 › HR 연동 사전 점검 (2026-09-14).
 *
 *   GET  /api/admin/hr-integration  → chat-server GET  /api/v1/admin/hr-integration  (지금 설정 — 읽기만)
 *   POST /api/admin/hr/test         → chat-server POST /api/v1/admin/hr/test         (사원번호 하나로 붙어 본다)
 *
 * ★ 설정을 **바꾸는 길은 여기 없다**(PUT/DELETE 를 부르지 않는다) — 이 화면은 점검 전용이다.
 * ★ API 키는 서버가 원문을 안 준다 — 가려진 값(`apiKeyMasked`)만 온다.
 */
import { apiClient } from './client';

export type HrAdapterKind = 'standard' | 'sap_hcm' | 'douzone_hrd' | 'custom' | 'none';

export type HrIntegrationView =
  | { configured: false; adapter: 'none'; enabled: false }
  | {
    configured: true;
    adapter: HrAdapterKind;
    baseUrl: string | null;
    apiKeyMasked: string | null;
    timeoutSec: number;
    enabled: boolean;
  };

/** 화면이 그리는 네 갈래 + 「연동이 꺼져 있음」. 서버 응답을 이 하나로 접는다. */
export type HrProbeResult =
  | { kind: 'found'; employee: { name: string; department: string | null; position: string | null } }
  | { kind: 'not_found' }
  | { kind: 'auth' }
  | { kind: 'unavailable' }
  | { kind: 'not_configured' };

export async function fetchHrIntegration(): Promise<HrIntegrationView> {
  const { data } = await apiClient.get<HrIntegrationView>('/admin/hr-integration');
  return data;
}

interface HrTestResponse {
  success: boolean;
  employee?: { name: string; department?: string | null; position?: string | null };
  reason?: 'NOT_FOUND' | 'AUTH' | 'UNAVAILABLE';
}

/**
 * 사원번호 하나로 HR 에 붙어 본다. 서버 400 `HR_NOT_CONFIGURED` 는 **오류가 아니라 결과**로 돌려준다
 * (「꺼져 있다」는 점검의 답이다). 그 밖의 실패(권한·네트워크)는 그대로 던진다.
 */
export async function probeHrEmployee(employeeId: string): Promise<HrProbeResult> {
  try {
    const { data } = await apiClient.post<HrTestResponse>('/admin/hr/test', { employee_id: employeeId });
    if (data.success && data.employee) {
      return {
        kind: 'found',
        employee: {
          name: data.employee.name,
          department: data.employee.department ?? null,
          position: data.employee.position ?? null,
        },
      };
    }
    if (data.reason === 'NOT_FOUND') return { kind: 'not_found' };
    if (data.reason === 'AUTH') return { kind: 'auth' };
    return { kind: 'unavailable' };
  } catch (e) {
    const res = (e as { response?: { status?: number; data?: { code?: string } } })?.response;
    if (res?.status === 400 && res.data?.code === 'HR_NOT_CONFIGURED') return { kind: 'not_configured' };
    throw e;
  }
}
